import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';

const testimonials = [
  {
    id: 'testimonial-1',
    name: 'Michael Rodriguez',
    role: 'CEO, TechStart Inc.',
    content: 'Yaenlinea Soft transformed our business operations with their custom software solution. Their team understood our needs perfectly and delivered beyond expectations.',
    rating: 5,
  },
  {
    id: 'testimonial-2',
    name: 'Sarah Thompson',
    role: 'Founder, GrowthLab',
    content: 'Working with the Yaenlinea team was a fantastic experience. They built our mobile app from scratch and the quality exceeded what we imagined. Highly recommend!',
    rating: 5,
  },
  {
    id: 'testimonial-3',
    name: 'David Chen',
    role: 'CTO, InnovateCo',
    content: 'The automation solutions they implemented saved us countless hours of manual work. Professional, reliable, and truly experts in their field.',
    rating: 5,
  },
];

const Testimonials = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <section
      id="testimonials"
      className="relative py-20 md:py-32 overflow-hidden"
      data-testid="testimonials-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#030305] via-[#0a0a15] to-[#030305]" />
      
      {/* Decorative gradient */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-1/2 bg-violet-600/5 rounded-full blur-[200px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-cyan-400 text-sm font-mono tracking-wider uppercase mb-4 block">
            Testimonials
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-testid="testimonials-heading">
            What Our Clients Say
          </h2>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
            Don't just take our word for it. Here's what businesses across Miami and beyond 
            have to say about working with us.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
        >
          {testimonials.map((testimonial) => (
            <motion.div
              key={testimonial.id}
              variants={itemVariants}
              className="testimonial-card rounded-2xl p-8 border border-white/10 hover:border-violet-500/30 transition-all duration-300 group"
              data-testid={testimonial.id}
            >
              {/* Quote icon */}
              <div className="mb-6">
                <Quote className="w-10 h-10 text-violet-500/30" />
              </div>

              {/* Content */}
              <p className="text-gray-300 leading-relaxed mb-6 text-sm md:text-base">
                "{testimonial.content}"
              </p>

              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                ))}
              </div>

              {/* Author */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-600 to-cyan-500 flex items-center justify-center">
                  <span className="text-white font-bold">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <div className="text-white font-medium">{testimonial.name}</div>
                  <div className="text-gray-500 text-sm">{testimonial.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
